import React from 'react'
import { Formik } from 'formik'
import * as yup from 'yup'
import styled from 'styled-components'
import { ModalInjectedProps, Modal, Button } from '@sketch/components'
import { useToast } from '@sketch/toasts'
import { SettingsTab } from './SettingsTab'
import { ContentsTab } from './ContentsTab'
import {
  PreviewFormValues,
  PreviewFormProvider,
  usePreviewForm,
} from './usePreviewForm'
import { useCreatePreviewMutation } from '@sketch/gql-types'

const validationSchema = yup.object({
  previewName: yup.string().required('Preview name is required'),
  description: yup.string(),
  password: yup.string().when('passwordEnabled', {
    is: true,
    then: schema =>
      schema.required(
        'Password is required when password protection is enabled'
      ),
  }),
  previewExpiration: yup.string().when('previewExpirationEnabled', {
    is: true,
    then: schema =>
      schema.required('Date is required when preview expiration is enabled'),
  }),
  selectedUUIDs: yup
    .array(
      yup.object({
        identifier: yup.string().required(),
        type: yup.string().oneOf(['PAGE', 'PROTOTYPE']).required(),
      })
    )
    .min(1, 'At least one item must be selected'),
})

const initialValues: PreviewFormValues = {
  previewName: '',
  description: '',
  inspectEnabled: false,
  commentsEnabled: false,
  passwordEnabled: false,
  password: '',
  previewExpirationEnabled: false,
  previewExpiration: '',
  selectedUUIDs: [],
}

const ModalStyled = styled(Modal)`
  max-width: 640px;
`

interface PartialShareModalProps extends ModalInjectedProps {
  shareName: string
  shareIdentifier: string
  versionShortId: string
}

export const PartialShareModal = ({
  shareName,
  shareIdentifier,
  versionShortId,
  hideModal,
}: PartialShareModalProps) => {
  const { showToast } = useToast()

  const [createPreview] = useCreatePreviewMutation({
    onCompleted: ({ createPreview }) => {
      showToast(
        <>
          The Preview &quot;{createPreview.preview.name}&quot; has been
          successfully created.
        </>
      )

      hideModal()
    },
    onError: 'show-toast',
  })

  const handleSubmit = (values: PreviewFormValues) =>
    createPreview({
      variables: {
        shareIdentifier,
        name: values.previewName,
        description: values.description,
        inspectEnabled: values.inspectEnabled,
        commentsEnabled: values.commentsEnabled,
        content: values.selectedUUIDs,
      },
    })

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      validateOnMount={true}
      onSubmit={handleSubmit}
    >
      <ModalStyled onCancel={hideModal}>
        <PreviewFormProvider>
          <Modal.Body>
            <Modal.Tabs
              steps={[
                {
                  title: 'Settings',
                  content: <SettingsTab shareName={shareName} />,
                },
                {
                  title: 'Preview Contents',
                  content: (
                    <ContentsTab
                      shareIdentifier={shareIdentifier}
                      versionShortId={versionShortId}
                    />
                  ),
                },
              ]}
            />
          </Modal.Body>
          <Modal.Footer>
            <PreviewFormFooter hideModal={hideModal} />
          </Modal.Footer>
        </PreviewFormProvider>
      </ModalStyled>
    </Formik>
  )
}

const PreviewFormFooter = ({ hideModal }: { hideModal: () => void }) => {
  const { submitForm, isSubmitting, isValid } = usePreviewForm()

  return (
    <>
      <Button disabled={isSubmitting} onClick={() => hideModal()}>
        Cancel
      </Button>
      <Button
        variant="primary"
        onClick={submitForm}
        disabled={isSubmitting || !isValid}
      >
        {isSubmitting ? 'Creating...' : 'Create Preview'}
      </Button>
    </>
  )
}
